import * as React from 'react'
import { styled, useTheme } from '@mui/material/styles'
import {
  AppBar as MuiAppBar,
  Box,
  Button,
  Link,
  Menu,
  MenuItem,
  AppBarProps as MuiAppBarProps,
  useMediaQuery,
  SvgIcon,
} from '@mui/material'
import { useMatch } from 'react-router-dom'
import logoPNG from 'assets/img/logo-yellow-bellow-g.png'
import { useTranslation } from 'react-i18next'
import { LANGUAGES } from 'helpers/constants'
import { SupportedLanguage } from 'types'
import GR from 'assets/svg/flags/GR.svg'
import EN from 'assets/svg/flags/EN.svg'
import RenderOnAdmin from 'components/access/RenderOnAdmin'

import { ReactComponent as MenuIconSVG } from 'assets/svg/menu/menu_icon.svg'
import { ReactComponent as MenuIconMobileSVG } from 'assets/svg/menu/menu_icon_mobile.svg'
import StyledMenu from './Menu'

interface AppBarProps extends MuiAppBarProps {
  open?: boolean
  width: number
  onOpen: () => void
  onClose: () => void
}

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) =>
    prop !== 'open' &&
    prop !== 'width' &&
    prop !== 'onOpen' &&
    prop !== 'onClose',
})<AppBarProps>(({ theme, open, width }) => ({
  boxShadow: 'none',
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
}))

const LanguageMenu = () => {
  const { t, i18n } = useTranslation()
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
  const open = Boolean(anchorEl)

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }
  const handleClose = () => {
    setAnchorEl(null)
  }
  const handleClickLanguage = (language: SupportedLanguage) => {
    i18n.changeLanguage(language)
    setAnchorEl(null)
  }

  return (
    <RenderOnAdmin>
      <div>
        <Button
          id="basic-button"
          aria-controls={open ? 'basic-menu' : undefined}
          aria-haspopup="true"
          aria-expanded={open ? 'true' : undefined}
          onClick={handleClick}
          color="secondary"
        >
          <Box
            component="img"
            src={i18n.language === LANGUAGES.LANGUAGE_GR ? GR : EN}
            sx={{
              width: 40,
              borderRadius: 40,
              p: 1,
            }}
          />
        </Button>
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
          sx={{ zIndex: 2000 }}
        >
          <MenuItem onClick={() => handleClickLanguage(LANGUAGES.LANGUAGE_GR)}>
            GR
          </MenuItem>
          <MenuItem onClick={() => handleClickLanguage(LANGUAGES.LANGUAGE_EN)}>
            EN
          </MenuItem>
        </Menu>
      </div>
    </RenderOnAdmin>
  )
}

export default function DashboardAppBar(props: AppBarProps) {
  const { open, width, onOpen, onClose } = props

  const [openMenu, setOpenMenu] = React.useState<boolean>(false)

  const handleOpenMenu = () => {
    setOpenMenu(true)
  }
  const handleCloseMenu = () => {
    setOpenMenu(false)
  }
  const theme = useTheme()
  const matchSearch = useMatch('/search')
  const matchDetail = useMatch('/property-ads/:adId')
  const matchPublicAgents = useMatch('/estate-agents')
  const matchPublicAgentsId = useMatch('estate-agents/:id')
  const matchCalculator = useMatch('/calculator')
  const matchTerms = useMatch('/terms')
  const matchContact = useMatch('/contact-us')
  const match404 = useMatch('404')
  const matchRecents = useMatch('/recent')
  const matchRealtorsInfo = useMatch('/realtors-info')

  const matchesSM = useMediaQuery(theme.breakpoints.down('lg'))

  const MATCHES_INTERNAL =
    matchSearch ||
    matchDetail ||
    matchCalculator ||
    matchTerms ||
    matchCalculator ||
    matchRecents ||
    matchPublicAgents ||
    match404 ||
    matchContact ||
    matchRealtorsInfo ||
    matchPublicAgentsId

  return (
    <AppBar
      open={open}
      width={width}
      onOpen={onOpen}
      onClose={onClose}
      position="fixed"
      sx={{
        top: 0,
        height: { xs: '57px', sm: '54px', lg: '98px' },
        px: { xs: '12px', sm: '26px', lg: '100px' },
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        zIndex: (theme) => theme.zIndex.drawer + 1,
        boxShadow: '0px 3px 5px rgba(0, 0, 0, 0.4)',
      }}
    >
      {/* <Toolbar> */}
      <Link
        href={process.env.REACT_APP_REDIRECT_SEARCH_APP_URL}
        sx={{ position: 'absolute', top: { xs: '4px', sm: '3px', lg: '8px' } }}
      >
        <Box
          component="img"
          src={logoPNG}
          alt="logo"
          sx={{
            width: { xs: '172px', sm: '190px', lg: '282px' },
            height: { xs: '64.5px', sm: '71px', lg: '105px' },
          }}
        />
      </Link>
      <Box sx={{ flexGrow: 1, display: { xs: 'flex' } }} />
      <Box
        sx={{
          flexGrow: 0,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexWrap: 'nowrap',
          bgcolor: 'transparent',
          cursor: 'pointer',
          '&:hover': {
            bgcolor: 'transparent',
          },
        }}
        onClick={handleOpenMenu}
      >
        {matchesSM ? (
          <SvgIcon
            alt="menu"
            component={MenuIconMobileSVG}
            inheritViewBox
            sx={{
              fill: 'black',
              width: { xs: 40 },
              height: { xs: 40 },
              '&:hover': {
                bgcolor: 'transparent',
                fill: (theme) =>
                  MATCHES_INTERNAL
                    ? theme.palette.primary.main
                    : theme.palette.common.white,
              },
            }}
          />
        ) : (
          <SvgIcon
            alt="menu"
            component={MenuIconSVG}
            inheritViewBox
            sx={{
              fontFamily: 'PF Bague Sans Pro Bold',
              fill: 'black',
              bgcolor: 'transparent',
              width: { sm: 160 },
              height: { sm: 50 },
              mr: 1,
              mb: -2,
              '&:hover': {
                bgcolor: 'transparent',
                fill: (theme) =>
                  MATCHES_INTERNAL
                    ? theme.palette.primary.main
                    : theme.palette.common.white,
              },
            }}
          />
        )}
      </Box>
      {openMenu ? (
        <StyledMenu open={openMenu} onClose={handleCloseMenu} />
      ) : null}
    </AppBar>
  )
}
